export class IntegrationsApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** D727: the object store's real bucket slot (masked). */
    /** D854: the push (FCM) slot, masked. */
    getPushIntegration() {
        return this.client.request({ method: 'GET', path: '/v1/config/integrations/push', auth: true });
    }
    /** D854: set or rotate the push service-account key (sealed write-only) and the switch. */
    setPushFcm(input) {
        return this.client.request({ method: 'PUT', path: '/v1/config/integrations/push/fcm', body: input, auth: true });
    }
    getStorageConfig() {
        return this.client.request({ method: 'GET', path: '/v1/config/integrations/storage', auth: true });
    }
    /** D727: set the account id, the bucket and the switch, and (optionally) rotate the access key pair (sealed write-only). */
    setStorageConfig(input) {
        return this.client.request({ method: 'PUT', path: '/v1/config/integrations/storage', body: input, auth: true });
    }
    /** D727: write, read back and delete a probe object in the bucket (works before switching on). */
    testStorage() {
        return this.client.request({ method: 'POST', path: '/v1/config/integrations/storage/test', body: {}, auth: true });
    }
    /** D631: the OpenRouter slot and the named self-hosted speech endpoints (masked). */
    getAiCredentials(speechRefs = []) {
        const qs = speechRefs.length > 0 ? `?speech=${encodeURIComponent(speechRefs.join(','))}` : '';
        return this.client.request({ method: 'GET', path: `/v1/config/integrations/ai${qs}`, auth: true });
    }
    /** D631: set or rotate the OpenRouter key (sealed write-only). */
    setAiOpenrouterSlot(input) {
        return this.client.request({ method: 'PUT', path: '/v1/config/integrations/ai/openrouter', body: input, auth: true });
    }
    /** D631: set a self-hosted speech endpoint under its credential name. */
    setAiSpeechSlot(ref, input) {
        return this.client.request({ method: 'PUT', path: `/v1/config/integrations/ai/speech/${encodeURIComponent(ref)}`, body: input, auth: true });
    }
    /** D436: every geo vendor slot (masked). */
    getGeoProviders() {
        return this.client.request({ method: 'GET', path: '/v1/config/integrations/geo', auth: true });
    }
    /** D436: set one geo vendor slot (the key is sealed write-only). */
    setGeoProvider(vendor, input) {
        return this.client.request({ method: 'PUT', path: `/v1/config/integrations/geo/${vendor}`, body: input, auth: true });
    }
    /** D436: probe the self-hosted router at a market centre (or a point). */
    probeGeoRouter(input) {
        return this.client.request({ method: 'POST', path: '/v1/config/integrations/geo/osrm/health', body: input, auth: true });
    }
    /** D436: calls, cache hits, failures and cost per geo provider over a window (default: the last 30 days). */
    getGeoUsage(input = {}) {
        const q = new URLSearchParams();
        if (input.since !== undefined)
            q.set('since', input.since);
        if (input.until !== undefined)
            q.set('until', input.until);
        if (input.marketId !== undefined)
            q.set('marketId', input.marketId);
        const qs = q.toString();
        return this.client.request({ method: 'GET', path: `/v1/config/integrations/geo/usage${qs !== '' ? `?${qs}` : ''}`, auth: true });
    }
    /** D870: the maps spend of the vendor's billing month (month to date per vendor and SKU, the free allowance, the projection range, the caps, the exchange rate). */
    getGeoSpend(input = {}) {
        const q = new URLSearchParams();
        if (input.windowDays !== undefined)
            q.set('windowDays', String(input.windowDays));
        if (input.country !== undefined)
            q.set('country', input.country);
        const qs = q.toString();
        return this.client.request({ method: 'GET', path: `/v1/config/integrations/geo/spend${qs !== '' ? `?${qs}` : ''}`, auth: true });
    }
    /** D471: the map tile source (a vault slot with no key — the operator's browser loads the tiles). */
    getMapTiles() {
        return this.client.request({ method: 'GET', path: '/v1/config/integrations/maps', auth: true });
    }
    /** D471: set the map tile source (an XYZ URL template with {z}/{x}/{y}, the attribution and the zoom range). */
    setMapTiles(input) {
        return this.client.request({ method: 'PUT', path: '/v1/config/integrations/maps/tiles', body: input, auth: true });
    }
    /** D343: the server-derived state the guided OTP setup wizard resumes from. */
    getOtpDeliveryStatus() {
        return this.client.request({ method: 'GET', path: '/v1/config/integrations/otp-delivery/status', auth: true });
    }
    /** D343: seal the Meta webhook app secret and/or verify token into the vault (write-only). */
    setWhatsappWebhook(input) {
        return this.client.request({ method: 'PUT', path: '/v1/config/integrations/whatsapp/webhook', body: input, auth: true });
    }
    /** D343: read-only check of the phone number id + templates against Meta (sends nothing). */
    checkWhatsappWithMeta() {
        return this.client.request({ method: 'POST', path: '/v1/config/integrations/whatsapp/meta-check', body: {}, auth: true });
    }
    /** D343: flip `enabled` on a stored, complete slot (pinned to the reviewed version when given). */
    setOtpChannelEnabled(channel, input) {
        return this.client.request({ method: 'POST', path: `/v1/config/integrations/${channel}/enabled`, body: input, auth: true });
    }
    /** D343: the tracked outcome of one test-send (receipts for WhatsApp; SMS stays ACCEPTED). */
    getTestSend(channel, providerMessageId) {
        return this.client.request({ method: 'GET', path: `/v1/config/integrations/${channel}/test/${encodeURIComponent(providerMessageId)}`, auth: true });
    }
    getWhatsappConfig() {
        return this.client.request({ method: 'GET', path: '/v1/config/integrations/whatsapp', auth: true });
    }
    setWhatsappConfig(input) {
        return this.client.request({ method: 'PUT', path: '/v1/config/integrations/whatsapp', body: input, auth: true });
    }
    /** Send one real Authentication template (a throwaway code) to a phone the operator owns. */
    sendTestWhatsapp(input) {
        return this.client.request({ method: 'POST', path: '/v1/config/integrations/whatsapp/test', body: input, auth: true });
    }
    getSmsConfig() {
        return this.client.request({ method: 'GET', path: '/v1/config/integrations/sms', auth: true });
    }
    setSmsConfig(input) {
        return this.client.request({ method: 'PUT', path: '/v1/config/integrations/sms', body: input, auth: true });
    }
    sendTestSms(input) {
        return this.client.request({ method: 'POST', path: '/v1/config/integrations/sms/test', body: input, auth: true });
    }
    /** The masked email-integration status + config (never the key). */
    getEmailConfig() {
        return this.client.request({ method: 'GET', path: '/v1/config/integrations/email', auth: true });
    }
    /** Set the email config and (optionally) rotate the key; the key is sealed into the KEK-encrypted vault. */
    setEmailConfig(input) {
        return this.client.request({ method: 'PUT', path: '/v1/config/integrations/email', body: input, auth: true });
    }
    /** Send a test email through the configured provider (to the given address, or the operator's own email). */
    sendTestEmail(input = {}) {
        return this.client.request({ method: 'POST', path: '/v1/config/integrations/email/test', body: input, auth: true });
    }
}
//# sourceMappingURL=integrations.js.map